import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import ScrollToTop from "../buttons/ScrollToTop";
import FloatingSave from "../buttons/FloatingSave";
import FloatingComment from "./../buttons/FloatingComment";
import Select from "./Select";
import { sendEmail, writeProcessLog } from "../services/soar";
import { openPDF, openPDFWithStyle } from "../../scripts/helperScripts";
import { confirmAlert } from "react-confirm-alert";
import { useLocation } from "react-router-dom";

function FormButtonBar({
  config,
  onSave,
  onSignClicked,
  validate,
  customValidate,
  onClone,
  onDelete,
  onShare,
  onComment,
  onCompare,
  signed,
  finalSigned,
  commentsCount,
  sectionsToNavigate,
  beforePrintFunction,
  newestVersion,
  pdfRef,
  filename,
  printStyle,
  printColor,
  printReport,
  hidePrint,
}) {
  const [floatingButtonsVisible, setFloatingButtonsVisible] = useState(false);
  const [toolboxCollapsed, setToolboxCollapsed] = useState(false);
  const [triggerPDF, setTriggerPDF] = useState(false);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const location = useLocation();

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility);

    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pdfRef) pdfRef.current = false;
    };
  }, [pdfRef]);

  useEffect(() => {
    if (printReport) {
      setTimeout(() => {
        setTriggerPDF(true);
      }, 50);
    }
  }, [printReport]);

  useEffect(() => {
    if (triggerPDF === true && pdfRef?.current) {
      const doOpen = async () => {
        const toastId = toast.loading("Generating PDF...", {
          type: toast.TYPE.INFO,
        });

        try {
          if (printStyle) {
            await openPDFWithStyle(
              pdfRef.current,
              filename,
              printStyle,
              printColor
            );
          } else {
            await openPDF(pdfRef.current, filename, printColor);
          }

          toast.update(toastId, {
            render: "PDF successfully generated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        } catch {
          toast.update(toastId, {
            render: "An error occurred while generating the PDF.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        }

        if (printReport) {
          window.close();
        }
      };
      doOpen();
      setTriggerPDF(false);
    }
  }, [triggerPDF, printReport, printStyle, pdfRef, filename, printColor]);

  const sendToPDF = useCallback(() => {
    if (pdfRef && pdfRef.current) {
      console.log("PDF Ref", pdfRef.current);
      setTriggerPDF(true);
    }
  }, [pdfRef]);

  // Show button when page is scorlled upto given distance
  const toggleVisibility = () => {
    if (window.pageYOffset > 1) {
      setFloatingButtonsVisible(true);
    } else {
      setFloatingButtonsVisible(false);
    }
  };

  const scrollToSection = (sectionId) => {
    window.scrollTo({
      top: document.getElementById(sectionId).offsetTop - 50,
      behavior: "smooth",
    });
  };

  const getErrors = (errors, startingIndex = 0) => {
    return (
      <div className="p-2">
        Form cannot be signed until all required fields have a value.
        <br />
        <br />
        The following fields did not pass validation:
        <ul>
          {Object.keys(errors)
            .slice(startingIndex, startingIndex + 10)
            .map((propName, index) => {
              return <li key={index}>{errors[propName]}</li>;
            })}
        </ul>
        {Object.keys(errors).length > startingIndex + 10 ? (
          <button
            className="btn btn-primary"
            onClick={() => {
              toast.error(getErrors(errors, startingIndex + 10));
            }}
          >
            More validation errors...
          </button>
        ) : null}
      </div>
    );
  };

  const getDeactivateReason = (
    promptText = "This deactivation request must go through SOAR Support. Please, provide a reason:"
  ) => {
    const reason = window.prompt(promptText);
    if (reason === "")
      return getDeactivateReason(`A reason must be provided.\r\n${promptText}`);
    else return reason;
  };

  return (
    <div className="bg-safy3-1 p-2 clearfix d-print-none rounded-bottom">
      <div className="float-end">
        <i
          title="Save"
          role="button"
          className="far fa-save fa-2x text-white mx-1"
          onClick={() => {
            onSave(null, null, null, null);
          }}
        ></i>
        {config.sign?.display !== false ? (
          <i
            title="Sign"
            role="button"
            className="fas fa-file-signature fa-2x text-white mx-1"
            onClick={(e) => {
              let validateResults = validate();
              if (customValidate)
                validateResults = customValidate(validateResults);

              if (Object.keys(validateResults).length === 0) {
                onSignClicked(e);
              } else {
                toast.error(getErrors(validateResults));
              }
            }}
          ></i>
        ) : null}
        {config.clone?.display !== false ? (
          <i
            title="Clone"
            role="button"
            className={`far fa-clone fa-2x mx-1 ${
              !config.cloneVersion && newestVersion === false
                ? "text-black-50"
                : "text-white"
            }`}
            onClick={(e) => {
              console.log("Clone Clicked");
              if (!config.cloneVersion && newestVersion === false) {
                toast.warning(
                  "Unable to clone because a newer version of this form exists. Please create a new form and copy over any relevant information.",
                  {
                    theme: "light",
                    isLoading: false,
                    autoClose: false,
                    closeOnClick: true,
                  }
                );
              } else if (
                window.confirm("Are you sure you want to clone the form?")
              ) {
                onClone(e);
              }
            }}
          ></i>
        ) : null}
        {onDelete && config.delete?.display !== false ? (
          <i
            title="Delete"
            role="button"
            className="far fa-trash-alt fa-2x text-white mx-1"
            onClick={(e) => {
              console.log("Delete Clicked");
              if (!signed) {
                if (window.confirm("Are you sure you want to delete?")) {
                  onDelete(e);
                }
              } else {
                const reasonForDeactivate = getDeactivateReason();
                if (
                  reasonForDeactivate !== undefined &&
                  reasonForDeactivate !== null
                ) {
                  sendEmail(
                    ["support@safy.org"],
                    safyInfo.user.email,
                    "Formsy Submission Deactivation Request",
                    `<p>Link: <a href="${window.location.href}">${window.location.href}</a><br/>User: ${safyInfo.user.displayName}, ${safyInfo.user.domainName}<br/>Reason: ${reasonForDeactivate}</p>`
                  );
                }
              }
            }}
          ></i>
        ) : null}
        {onDelete && safyInfo.user.modules.includes("Admin") ? (
          <i
            title="Admin Delete"
            role="button"
            className="far fa-times-circle fa-2x text-danger mx-1"
            onClick={(e) => {
              console.log("Admin Delete Clicked");
              const reasonForDeactivate = getDeactivateReason("Reason:");
              if (
                reasonForDeactivate !== undefined &&
                reasonForDeactivate !== null
              ) {
                const databaseOrg =
                  process.env.REACT_APP_ENVIRONMENT === "Production"
                    ? "ReactLive"
                    : "ReactDev";

                const submissionId = location.pathname.split("/").pop();

                const deleteInfo = {
                  SubmissionId: submissionId,
                  User: safyInfo.user.domainName,
                  Reason: reasonForDeactivate,
                };

                const logData = {
                  Type: "Delete Formsy Submission",
                  Location: databaseOrg,
                  Description: location.pathname,
                  Data: JSON.stringify(deleteInfo),
                  ObjectReferenceType: "Formsy Delete",
                  ObjectReferenceId: submissionId,
                };

                writeProcessLog(logData);
                onDelete(e);
              }
            }}
          ></i>
        ) : null}
        {config.share?.display !== false ? (
          <i
            title="Share"
            role="button"
            className="far fa-share-square fa-2x text-white mx-1"
            onClick={() => {
              const allowShare =
                process.env.REACT_APP_ENVIRONMENT === "Production" ||
                safyInfo.user.modules.includes("Admin");

              if (allowShare) {
                onShare();
              } else {
                confirmAlert({
                  title: "Share Form",
                  message: "Are you sure you want to share this form in Test?",
                  buttons: [
                    {
                      label: "Yes",
                      onClick: () => {
                        onShare();
                      },
                    },
                    {
                      label: "No",
                      onClick: () => {
                        /* do nothing */
                      },
                    },
                  ],
                });
              }
            }}
          ></i>
        ) : null}
        {!finalSigned && (
          <span className="fa-layers fa-fw">
            <i
              title="Comment"
              role="button"
              className="far fa-comment fa-2x text-white mx-1"
              onClick={(e) => {
                onComment();
              }}
            ></i>
            <span
              className="fa-layers-text fa-inverse"
              data-fa-transform="shrink-8"
            >
              {commentsCount}
            </span>
          </span>
        )}
        {config.compare && config.compare.display && (
          <i
            title="Compare"
            role="button"
            style={{ fontSize: "1.5em" }}
            className="fas fa-equals text-white mx-1"
            onClick={() => onCompare()}
          ></i>
        )}
        {pdfRef ? (
          <i
            tile="Create PDF"
            role="button"
            className="fas fa-file-pdf fa-2x text-white mx-1"
            onClick={() => {
              if (beforePrintFunction) {
                beforePrintFunction();
                setTimeout(() => sendToPDF(), 5);
              } else sendToPDF();
            }}
          ></i>
        ) : null}
        {!hidePrint ? (
          <i
            title="Print"
            role="button"
            className="fas fa-print fa-2x text-white mx-1"
            onClick={() => {
              if (beforePrintFunction) {
                beforePrintFunction();
                setTimeout(() => window.print(), 5);
              } else window.print();
            }}
          ></i>
        ) : null}
      </div>
      {floatingButtonsVisible ? (
        <div className="floating-toolbox">
          <div className="clearfix mb-3">
            <div className="float-start">
              <i
                className={`fas fa-2x me-3 mt-2 fa-chevron-${
                  toolboxCollapsed ? "left" : "right"
                }`}
                onClick={() => {
                  setToolboxCollapsed(!toolboxCollapsed);
                }}
              ></i>
            </div>
            {!toolboxCollapsed && (
              <>
                {config.comment.display && !finalSigned && (
                  <div className="float-end">
                    <FloatingComment onComment={onComment} />
                  </div>
                )}
                <div className="float-end">
                  <FloatingSave
                    onSave={() => {
                      onSave(null, null, null, null);
                    }}
                  />
                </div>
                <div className="float-end">
                  <ScrollToTop />
                </div>
              </>
            )}
          </div>
          {!toolboxCollapsed &&
          sectionsToNavigate !== undefined &&
          sectionsToNavigate.length > 0 ? (
            <div
              className="row rounded bg-success me-2 p-1 text-white fw-bold"
              style={{ width: "182px" }}
            >
              <Select
                label="Navigation:"
                name="SelectSection"
                options={sectionsToNavigate}
                className="w-100"
                onChange={(e) => scrollToSection(e.currentTarget.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default FormButtonBar;
